import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '@/contexts/ThemeContext';
import { getCommandPaletteShortcut, formatShortcut } from '@/utils/osDetection';

export interface Command {
    id: string;
    label: string;
    icon: string;
    description?: string;
    keywords?: string[];
    action: () => void;
    shortcut?: string[];  // Array of keys like ['cmd', 'shift', 'u']
    roles?: string[];  // Array of roles that can access this command (e.g., ['admin', 'coach'])
}

interface CommandPaletteProps {
    isOpen: boolean;
    onClose: () => void;
    commands: Command[];
}

/**
 * Modern Command Palette component with search and keyboard navigation
 * Inspired by tools like Raycast, Spotlight, and VS Code Command Palette
 */
export const CommandPalette: React.FC<CommandPaletteProps> = ({ isOpen, onClose, commands }) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedIndex, setSelectedIndex] = useState(0);
    const { isDark } = useTheme();
    const inputRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLDivElement>(null);

    /**
     * Enhanced fuzzy search with relevance scoring
     * Implements SQL-like %LIKE% matching with intelligent ranking
     */
    const getMatchScore = (cmd: Command, query: string): number => {
        const q = query.toLowerCase();
        const label = cmd.label.toLowerCase();
        const description = cmd.description?.toLowerCase() || '';
        const keywords = cmd.keywords || [];

        // No match
        if (!label.includes(q) && !description.includes(q) && !keywords.some(k => k.toLowerCase().includes(q))) {
            return 0;
        }

        let score = 0;

        // Label matching (highest priority)
        if (label === q) {
            score += 1000; // Exact match
        } else if (label.startsWith(q)) {
            score += 500; // Starts with query
        } else if (label.includes(q)) {
            score += 300; // Contains query
            // Bonus for word boundary match
            if (label.includes(` ${q}`)) {
                score += 100;
            }
        }

        // Description matching (medium priority)
        if (description.includes(q)) {
            score += 50;
            // Bonus if query is at start of description
            if (description.startsWith(q)) {
                score += 25;
            }
        }

        // Keyword matching (lower priority, but still valuable)
        keywords.forEach(keyword => {
            const kw = keyword.toLowerCase();
            if (kw === q) {
                score += 200; // Exact keyword match
            } else if (kw.startsWith(q)) {
                score += 100; // Keyword starts with query
            } else if (kw.includes(q)) {
                score += 30; // Keyword contains query
            }
        });

        // Bonus for shorter labels (prefer concise matches)
        if (score > 0 && label.length < 30) {
            score += Math.floor((30 - label.length) / 2);
        }

        return score;
    };

    // Filter and sort commands based on search query with relevance scoring
    const filteredCommands = searchQuery.trim() === ''
        ? commands // No search query - show all commands
        : commands
            .map(cmd => ({
                command: cmd,
                score: getMatchScore(cmd, searchQuery)
            }))
            .filter(item => item.score > 0) // Only include matches
            .sort((a, b) => b.score - a.score) // Sort by relevance (best first)
            .map(item => item.command);

    // Reset selected index when filtered commands change
    useEffect(() => {
        setSelectedIndex(0);
    }, [searchQuery]);

    // Focus input when opened
    useEffect(() => {
        if (isOpen) {
            inputRef.current?.focus();
            setSearchQuery('');
            setSelectedIndex(0);
        }
    }, [isOpen]);

    // Handle keyboard navigation
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (!isOpen) return;

            switch (e.key) {
                case 'ArrowDown':
                    e.preventDefault();
                    setSelectedIndex(prev =>
                        prev < filteredCommands.length - 1 ? prev + 1 : prev
                    );
                    break;
                case 'ArrowUp':
                    e.preventDefault();
                    setSelectedIndex(prev => prev > 0 ? prev - 1 : 0);
                    break;
                case 'Enter':
                    e.preventDefault();
                    if (filteredCommands[selectedIndex]) {
                        filteredCommands[selectedIndex].action();
                        onClose();
                    }
                    break;
                case 'Escape':
                    e.preventDefault();
                    onClose();
                    break;
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, filteredCommands, selectedIndex, onClose]);

    // Scroll selected item into view
    useEffect(() => {
        if (listRef.current) {
            const selectedElement = listRef.current.children[selectedIndex] as HTMLElement;
            selectedElement?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
    }, [selectedIndex]);

    if (!isOpen) return null;

    // Render in a portal to avoid being constrained by ancestor stacking/containing contexts
    return createPortal(
        <div
            className="fixed inset-0 z-[9999] flex items-start justify-center pt-[15vh] px-4 bg-black/50 backdrop-blur-sm"
            onClick={onClose}
        >
            {/* Command Palette */}
            <div
                className={`relative w-full max-w-2xl ${isDark ? 'bg-gray-800' : 'bg-white'
                    } rounded-2xl shadow-2xl overflow-hidden animate-scale-in`}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Search Input */}
                <div className={`flex items-center gap-3 px-4 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'
                    }`}>
                    <svg
                        className="w-5 h-5 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                        />
                    </svg>
                    <input
                        ref={inputRef}
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Type a command or search..."
                        className={`flex-1 bg-transparent outline-none text-lg ${isDark ? 'text-gray-100 placeholder-gray-500' : 'text-gray-900 placeholder-gray-400'
                            }`}
                    />
                    <kbd className={`hidden sm:block px-2 py-1 text-xs rounded ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'
                        }`}>
                        ESC
                    </kbd>
                </div>

                {/* Command List */}
                <div
                    ref={listRef}
                    className={`max-h-[400px] overflow-y-auto ${isDark ? 'bg-gray-800' : 'bg-white'
                        }`}
                >
                    {filteredCommands.length === 0 ? (
                        <div className="px-4 py-12 text-center">
                            <div className={`text-5xl mb-3`}>🔍</div>
                            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                No commands found for "{searchQuery}"
                            </p>
                        </div>
                    ) : (
                        filteredCommands.map((cmd, index) => (
                            <button
                                key={cmd.id}
                                onClick={() => {
                                    cmd.action();
                                    onClose();
                                }}
                                onMouseEnter={() => setSelectedIndex(index)}
                                className={`w-full flex items-center gap-4 px-4 py-3 transition-all duration-150 ${index === selectedIndex
                                    ? isDark
                                        ? 'bg-red-900/30 border-l-4 border-red-500'
                                        : 'bg-red-50 border-l-4 border-red-600'
                                    : 'border-l-4 border-transparent hover:' + (isDark ? 'bg-gray-700/50' : 'bg-gray-50')
                                    }`}
                            >
                                <div className="text-3xl">{cmd.icon}</div>
                                <div className="flex-1 text-left">
                                    <div className={`font-medium ${isDark ? 'text-gray-100' : 'text-gray-900'
                                        }`}>
                                        {cmd.label}
                                    </div>
                                    {cmd.description && (
                                        <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'
                                            }`}>
                                            {cmd.description}
                                        </div>
                                    )}
                                </div>
                                {cmd.shortcut && (
                                    <kbd className={`hidden sm:block px-2 py-1 text-xs rounded font-mono ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'
                                        }`}>
                                        {formatShortcut(cmd.shortcut)}
                                    </kbd>
                                )}
                            </button>
                        ))
                    )}
                </div>

                {/* Footer Hint */}
                {filteredCommands.length > 0 && (
                    <div className={`flex items-center justify-center gap-4 px-4 py-3 text-xs border-t ${isDark ? 'bg-gray-900/50 border-gray-700 text-gray-400' : 'bg-gray-50 border-gray-200 text-gray-500'
                        }`}>
                        <span className="flex items-center gap-1">
                            <kbd className={`px-1.5 py-0.5 rounded ${isDark ? 'bg-gray-700' : 'bg-white'}`}>↑</kbd>
                            <kbd className={`px-1.5 py-0.5 rounded ${isDark ? 'bg-gray-700' : 'bg-white'}`}>↓</kbd>
                            to navigate
                        </span>
                        <span className="flex items-center gap-1">
                            <kbd className={`px-1.5 py-0.5 rounded ${isDark ? 'bg-gray-700' : 'bg-white'}`}>↵</kbd>
                            to select
                        </span>
                        <span className="flex items-center gap-1">
                            <kbd className={`px-1.5 py-0.5 rounded ${isDark ? 'bg-gray-700' : 'bg-white'}`}>ESC</kbd>
                            to close
                        </span>
                    </div>
                )}
            </div>

            <style>{`
                @keyframes scale-in {
                    from {
                        opacity: 0;
                        transform: scale(0.95) translateY(-10px);
                    }
                    to {
                        opacity: 1;
                        transform: scale(1) translateY(0);
                    }
                }
                .animate-scale-in {
                    animation: scale-in 0.15s ease-out;
                }
            `}</style>
        </div>,
        document.body
    );
};

